import { ElementType, Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import DashboardLayout from '../layouts/dashboard';
// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
// components
import { RoleType } from '../@types/lms/enums';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },

        { path: 'login-unprotected', element: <Login /> },
        // { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    //LMS ROUTES
    {
      path: 'lvs',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/lvs/inspections/list" replace /> },
        { path: 'home', //element: <Home /> 
          element: (
              <RoleBasedGuard roles={[RoleType.Admin, RoleType.CompanyUser, RoleType.Coordinator]} hasContent={true}>
                <Home />
              </RoleBasedGuard>
            ),
        },
        {
          path: 'admin',
          children:[
          {
            element: (
              <RoleBasedGuard roles={['Admin']} hasContent={true}>
                <Navigate to="/lvs/admin/user/list" replace />
              </RoleBasedGuard>
            ),
            index: true,
          },
          {
            path: 'user',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/admin/user/list" replace />{' '}
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSUserList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSUserCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSUserEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'country',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/country/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCountryList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCountryCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCountryEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'company',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/company/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCompanyList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCompanyCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSCompanyView />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'working-days',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/working-days/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSWorkingDayList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSWorkingDayCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSWorkingDayEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'review-type',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/review-type/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSReviewTypeList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSReviewTypeCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSReviewTypeEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'questions',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/questions/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSQuestionList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSQuestionCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSQuestionEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'helptextclient',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/helptextclient/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsClientList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsClientCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsClientEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'helptextreviewer',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/helptextreviewer/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsReviewerList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsReviewerCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSHelpTextsReviewerEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'affiliate',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/affiliate/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSAffiliateList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSAffiliateCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <LMSAffiliateEdit />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'blog-category',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <Navigate to="/lvs/admin/blog-category/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <UserList />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <UserCreate />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard roles={['Admin']} hasContent={true}>
                    <UserCreate />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'pdf',
            children: [
              {
                element: (
                  <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
                    <Navigate to="/lvs/admin/pdf/list" replace />
                  </RoleBasedGuard>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
                    <LMSPDFEditor />
                  </RoleBasedGuard>
                ),
              },
            ],
          }
        ]
      },
        {
          path: 'blog',
          children: [
            {
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <Navigate to="/lvs/blog/list" replace />
                </RoleBasedGuard>
              ),
              index: true,
            },
            {
              path: 'list',
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <LMSBlogList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
                  <LMSBlogCreate />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/view',
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <LMSBlogView />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
                  <LMSBlogEdit />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'inspections',
          children: [
            {
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <Navigate to="/lvs/inspections/list" replace />
                </RoleBasedGuard>
              ),
              index: true,
            },
            {
              path: 'list',
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <LMSInspectionList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard roles={[RoleType.Admin, RoleType.CompanyUser]} hasContent={true}>
                  <LMSInspectionCreate />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard
                  roles={[
                    RoleType.Admin,
                    RoleType.CompanyUser,
                    RoleType.MultiVendorCustomer,
                    RoleType.Reviewer,
                    RoleType.Coordinator,
                    RoleType.LocalAffiliateCoordinator,
                    RoleType.LimitedAccessCoordinator,
                  ]}
                  hasContent={true}
                >
                  <LMSInspectionEdit />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        // {
        //   path: 'mail',
        //   children: [
        //     {
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <Navigate to="/lvs/mail/inbox" replace />
        //         </RoleBasedGuard>
        //       ),
        //       index: true,
        //     },
        //     {
        //       path: ':systemLabel',
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <LMSMail />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: ':systemLabel/:mailId',
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <LMSMail />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'label/:customLabel',
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <LMSMail />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'label/:customLabel/:mailId',
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <LMSMail />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //   ],
        // },
        // {
        //   path: 'documents',
        //   children: [
        //     {
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <Navigate to="/lvs/documents" replace />
        //         </RoleBasedGuard>
        //       ),
        //       index: true,
        //     },
        //     {
        //       path: 'all',
        //       element: (
        //         <RoleBasedGuard roles={['Admin', '']} hasContent={true}>
        //           <LMSDocuments />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //   ],
        // },
        {
          path: 'personal',
          children: [
            { element: <Navigate to="/lvs/personal" replace />, index: true },
            { path: 'profile', element: <LMSPersonal /> },
          ],
        },
      ],
    },

    // // Dashboard Routes
    // {
    //   path: 'dashboard',
    //   element: (
    //     <AuthGuard>
    //       <DashboardLayout />
    //     </AuthGuard>
    //   ),
    //   children: [
    //     { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
    //     { path: 'app', element: <GeneralApp /> },
    //     { path: 'ecommerce', element: <GeneralEcommerce /> },
    //     { path: 'analytics', element: <GeneralAnalytics /> },
    //     { path: 'banking', element: <GeneralBanking /> },
    //     { path: 'booking', element: <GeneralBooking /> },
    //
    //     {
    //       path: 'e-commerce',
    //       children: [
    //         { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
    //         { path: 'shop', element: <EcommerceShop /> },
    //         { path: 'product/:name', element: <EcommerceProductDetails /> },
    //         { path: 'list', element: <EcommerceProductList /> },
    //         { path: 'product/new', element: <EcommerceProductCreate /> },
    //         { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
    //         { path: 'checkout', element: <EcommerceCheckout /> },
    //       ],
    //     },
    //     {
    //       path: 'user',
    //       children: [
    //         { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
    //         { path: 'profile', element: <UserProfile /> },
    //         { path: 'cards', element: <UserCards /> },
    //         { path: 'list', element: <UserList /> },
    //         { path: 'new', element: <UserCreate /> },
    //         { path: ':name/edit', element: <UserCreate /> },
    //         { path: 'account', element: <UserAccount /> },
    //       ],
    //     },
    //     {
    //       path: 'invoice',
    //       children: [
    //         { element: <Navigate to="/dashboard/invoice/list" replace />, index: true },
    //         { path: 'list', element: <InvoiceList /> },
    //         { path: ':id', element: <InvoiceDetails /> },
    //         { path: ':id/edit', element: <InvoiceEdit /> },
    //         { path: 'new', element: <InvoiceCreate /> },
    //       ],
    //     },
    //     {
    //       path: 'blog',
    //       children: [
    //         { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
    //         { path: 'posts', element: <BlogPosts /> },
    //         { path: 'post/:title', element: <BlogPost /> },
    //         { path: 'new', element: <BlogNewPost /> },
    //       ],
    //     },
    //     {
    //       path: 'mail',
    //       children: [
    //         { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
    //         { path: 'label/:customLabel', element: <Mail /> },
    //         { path: 'label/:customLabel/:mailId', element: <Mail /> },
    //         { path: ':systemLabel', element: <Mail /> },
    //         { path: ':systemLabel/:mailId', element: <Mail /> },
    //       ],
    //     },
    //     {
    //       path: 'chat',
    //       children: [
    //         { element: <Chat />, index: true },
    //         { path: 'new', element: <Chat /> },
    //         { path: ':conversationKey', element: <Chat /> },
    //       ],
    //     },
    //     { path: 'calendar', element: <Calendar /> },
    //     { path: 'kanban', element: <Kanban /> },
    //     { path: 'permission-denied', element: <PermissionDenied /> },
    //   ],
    // },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
// const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// // GENERAL
// const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
// const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
// const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
// const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
// const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// // ECOMMERCE
// const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
// const EcommerceProductDetails = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductDetails'))
// );
// const EcommerceProductList = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductList'))
// );
// const EcommerceProductCreate = Loadable(
//   lazy(() => import('../pages/dashboard/EcommerceProductCreate'))
// );
// const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// // INVOICE
// const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
// const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
// const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
// const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// // BLOG
// const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
// const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
// const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// // USER
// const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
// const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// // APP
// const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
// const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
// const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
// const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// // TEST RENDER PAGE BY ROLE
// const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// // MAIN
// const HomePage = Loadable(lazy(() => import('../pages/Home')));
// const About = Loadable(lazy(() => import('../pages/About')));
// const Contact = Loadable(lazy(() => import('../pages/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
// const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
// const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
// const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
// const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

//LMS

//home
const Home = Loadable(lazy(() => import('../pages/lvs/Home')));

//user
const LMSUserList = Loadable(lazy(() => import('../pages/lvs/User/UserList')));
const LMSUserCreate = Loadable(lazy(() => import('../pages/lvs/User/UserCreate')));
const LMSUserEdit = Loadable(lazy(() => import('../pages/lvs/User/UserEdit')));

//working day
const LMSWorkingDayList = Loadable(lazy(() => import('../pages/lvs/WorkingDay/WorkingDayList')));
const LMSWorkingDayCreate = Loadable(
  lazy(() => import('../pages/lvs/WorkingDay/WorkingDayCreate'))
);
const LMSWorkingDayEdit = Loadable(lazy(() => import('../pages/lvs/WorkingDay/WorkingDayEdit')));

//country
const LMSCountryList = Loadable(lazy(() => import('../pages/lvs/Country/CountryList')));
const LMSCountryCreate = Loadable(lazy(() => import('../pages/lvs/Country/CountryCreate')));
const LMSCountryEdit = Loadable(lazy(() => import('../pages/lvs/Country/CountryEdit')));

//affiliate
const LMSAffiliateList = Loadable(lazy(() => import('../pages/lvs/Affiliate/AffiliateList')));
const LMSAffiliateEdit = Loadable(lazy(() => import('../pages/lvs/Affiliate/AffiliateEdit')));
const LMSAffiliateCreate = Loadable(lazy(() => import('../pages/lvs/Affiliate/AffiliateCreate')));

//article
const LMSBlogList = Loadable(lazy(() => import('../pages/lvs/Blog/BlogPosts')));
const LMSBlogView = Loadable(lazy(() => import('../pages/lvs/Blog/BlogPost')));
const LMSBlogCreate = Loadable(lazy(() => import('../pages/lvs/Blog/BlogNewPost')));
const LMSBlogEdit = Loadable(lazy(() => import('../pages/lvs/Blog/BlogEditPost')));

//article
const LMSCompanyList = Loadable(lazy(() => import('../pages/lvs/Company/CompanyList')));
const LMSCompanyView = Loadable(lazy(() => import('../pages/lvs/Company/CompanyEdit')));
const LMSCompanyCreate = Loadable(lazy(() => import('../pages/lvs/Company/CompanyCreate')));

// PDF
const LMSPDFEditor = Loadable(lazy(() => import('../pages/lvs/PDFEdit/PDFEdit')));

//QUESTION
const LMSQuestionList = Loadable(lazy(() => import('../pages/lvs/Question/QuestionList')));
const LMSQuestionEdit = Loadable(lazy(() => import('../pages/lvs/Question/QuestionEdit')));
const LMSQuestionCreate = Loadable(lazy(() => import('../pages/lvs/Question/QuestionCreate')));

//HELPTEXTSCLIENT
const LMSHelpTextsClientList = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsClient/HelpTextsClientList'))
);
const LMSHelpTextsClientEdit = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsClient/HelpTextsClientEdit'))
);
const LMSHelpTextsClientCreate = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsClient/HelpTextsClientCreate'))
);

//HELPTEXTSREVIEWER
const LMSHelpTextsReviewerList = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsReviewer/HelpTextsReviewerList'))
);
const LMSHelpTextsReviewerEdit = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsReviewer/HelpTextsReviewerEdit'))
);
const LMSHelpTextsReviewerCreate = Loadable(
  lazy(() => import('../pages/lvs/HelpTextsReviewer/HelpTextsReviewerCreate'))
);

//REVIEW TYPE
const LMSReviewTypeList = Loadable(lazy(() => import('../pages/lvs/ReviewType/ReviewTypeList')));
const LMSReviewTypeEdit = Loadable(lazy(() => import('../pages/lvs/ReviewType/ReviewTypeEdit')));
const LMSReviewTypeCreate = Loadable(
  lazy(() => import('../pages/lvs/ReviewType/ReviewTypeCreate'))
);

//INSPECTIONS
const LMSInspectionList = Loadable(lazy(() => import('../pages/lvs/Inspection/InspectionList')));
const LMSInspectionCreate = Loadable(
  lazy(() => import('../pages/lvs/Inspection/InspectionCreate'))
);
const LMSInspectionEdit = Loadable(lazy(() => import('../pages/lvs/Inspection/InspectionEdit')));

//MAIL
//const LMSMail = Loadable(lazy(() => import('../pages/lvs/Mail/Mail')));

const LMSPersonal = Loadable(lazy(() => import('../pages/lvs/Personal/Personal')));

//const LMSDocuments = Loadable(lazy(() => import('../pages/lvs/Documents/Documents')));
